/**
 * The `work` section — RO-3-03 (task #44296).
 *
 * RO-3-01's section map scopes this item to NINE pages
 * (`docs/rollover/inventory/pages.md` §4 "Work — 9 pages, owned by RO-3-03"):
 *
 *   work/page.tsx                 MID      → `/work`           (ModePage + dashboard)
 *   work/[...segments]/page.tsx  DELEGATOR → `/work/$`         (ModeSectionRoute)
 *   work/o/[uid]/page.tsx         DELEGATOR → `/work/o/$uid`    (redirect → /o/$uid?from=work)
 *   issues/page.tsx               REWRITE   → `/issues`         (+ Work ▸ Issues mount body)
 *   planner/page.tsx              DELEGATOR → `/planner`
 *   activity/page.tsx             DELEGATOR → `/activity`
 *   favorites/page.tsx            DELEGATOR → `/favorites`
 *   suggestions/page.tsx          DELEGATOR → `/suggestions`
 *   verdicts/page.tsx             DELEGATOR → `/verdicts`       (redirect → /findings, WP10c)
 *
 * Absorbed roots (issues/planner/activity/favorites/suggestions/verdicts) fold into
 * the work mode per redirects.ts — this item ports their standalone URLs so deep
 * links keep working; ModePage already hosts the mode-mounted views via
 * domain-mounts (RO-3-00c) once the pending bodies below are filled.
 *
 * ── What the transport change forced ──
 *
 * 1. Three server reads became endpoints (`modes.issuesBoard`, `modes.planner`,
 *    `modes.workDashboard`). The original pages called `@/projects/db` and
 *    friends in-process; a client route cannot. Live Next call sites stay on
 *    the server components (PRD D6).
 *
 * 2. Two task mutations the Issues board / Planner need (`moveTaskToStatus`,
 *    `setDueDate`) join the task-actions contract (RO-3-00g had setStatus +
 *    quickCreate). Live Next keeps the server actions.
 *
 * 3. `work/o/$uid` and `verdicts` are pure redirect stubs — same recipe as
 *    agent-redirects (RO-3-02).
 *
 * 4. `/work/$` was a `pending()` stub in dynamic-segments (RO-3-14). This item
 *    owns the real ModeSectionRoute body and REMOVES that stub so the tree
 *    never double-registers the path.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute, redirect } from "@tanstack/react-router";
import { resolveReviewVerdictEvidenceRedirect } from "./verification-legacy-redirects";
import { hopLeftoverLegacyRoute } from "./agents-unmount-hop";
import { PageHeader, SectionActions } from "@excellent/ui";
import { useSearch } from "@excellent/ui/router";
import { ModePage } from "../shared/modes/mode-page";
import { ModeSectionRoute } from "../shared/modes/mode-section-route";
import { CreateMenu } from "../shared/chrome/create-menu";
import { WorkDashboard } from "../shared/work/dashboard";
import { WorkPeriodFilter } from "../shared/work/period-filter";
import { coerceWorkPeriod } from "../shared/work/period";
import { IssuesPage } from "../shared/work/issues-page";
import { PlannerPage } from "../shared/work/planner-page";
import { FavoritesListClient } from "../shared/work/favorites-list";
import {
  OverrideQueue,
  RecordedOverrideDiffs,
} from "../shared/work/override-queue";
import { ActivityLive } from "../shared/activity/activity-live";
import { SuggestionsLive } from "../shared/activity/suggestions-live";
import { VerificationShell } from "../verification/shell";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { MODE_VIEWS, type ModeView } from "@/modes/modes-registry";

/** Flatten TanStack search values into the Record ModeSectionRoute expects. */
function flattenSearch(
  search: Record<string, string | string[] | undefined>,
): Record<string, string | string[] | undefined> {
  return search;
}

// ── search schemas ───────────────────────────────────────────────────────────

const workSearchSchema = routeSearchSchema({
  period: z.string().optional(),
});

const issuesSearchSchema = routeSearchSchema({
  section: z.string().optional(),
  range: z.string().optional(),
  to: z.string().optional(),
  status: z.string().optional(),
  assignee: z.string().optional(),
  project: z.string().optional(),
  empty: z.string().optional(),
  dept: z.string().optional(),
  mine: z.string().optional(),
});

const plannerSearchSchema = routeSearchSchema({
  month: z.string().optional(),
});

const verdictsSearchSchema = routeSearchSchema({
  dept: z.string().optional(),
  mine: z.string().optional(),
});

/** Convert the seam's URLSearchParams into a plain record for ModePage. */
function searchRecord(sp: URLSearchParams): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  sp.forEach((v, k) => {
    out[k] = v;
  });
  return out;
}

// ── /work — mode root with dashboard slot ────────────────────────────────────

function WorkIndexPage() {
  const sp = useSearch();
  const search = searchRecord(sp);
  const view = (MODE_VIEWS as readonly string[]).includes(search.view ?? "")
    ? (search.view as ModeView)
    : "dashboard";
  const area = search.area;
  const sub = search.sub;
  const isRoot = view === "dashboard" && !area;
  const period = coerceWorkPeriod(search.period);

  const dashboardSlot = isRoot ? <WorkDashboard period={period} /> : undefined;
  const titleToolbar = isRoot ? (
    <SectionActions primary={<CreateMenu />}>
      <WorkPeriodFilter value={period} />
    </SectionActions>
  ) : undefined;

  return (
    <ModePage
      mode="work"
      view={view}
      area={area}
      sub={sub}
      searchParams={search}
      dashboardSlot={dashboardSlot}
      titleToolbar={titleToolbar}
    />
  );
}

export const workIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work",
  validateSearch: workSearchSchema,
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
  component: WorkIndexPage,
});

// ── /work/$ — named section paths (/work/issues, /work/plan, …) ──────────────

function WorkSectionsPage() {
  const params = workSectionsRoute.useParams();
  const search = searchRecord(useSearch());
  // TanStack splat: `$` → `_splat` string; split into segments.
  const splat = (params as { _splat?: string })._splat ?? "";
  // RO-10-08: bare `/work` ranks the `/work/$` splat ABOVE the exact `/work`
  // index (TanStack treats an empty splat as a match). Hand off to the index
  // page so the mode masthead (h1 "Work") still renders — ModeSectionRoute
  // with zero segments paints an empty main.
  if (!splat) return <WorkIndexPage />;
  const segments = splat.split("/").filter(Boolean);
  return (
    <ModeSectionRoute
      mode="work"
      segments={segments}
      searchParams={flattenSearch(search)}
    />
  );
}

export const workSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/$",
  // #44601: empty splat outranks the bare `/work` index (deeper wildcard wins
  // when static scores tie). Reject so match + path generation target the index;
  // the render hand-off above stays as belt-and-suspenders for internal mounts.
  params: {
    parse: (raw) => {
      if (!raw._splat) return false;
      return raw;
    },
  },
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
  component: WorkSectionsPage,
});

// ── /work/o/$uid — legacy object URL → canonical /o/$uid ─────────────────────

export const workObjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/work/o/$uid",
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
});

// ── /issues ──────────────────────────────────────────────────────────────────

function IssuesStandalonePage() {
  const search = searchRecord(useSearch());
  return <IssuesPage searchParams={search} />;
}

export const issuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/issues",
  validateSearch: issuesSearchSchema,
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
  component: IssuesStandalonePage,
});

// ── /planner ─────────────────────────────────────────────────────────────────

function PlannerStandalonePage() {
  const search = searchRecord(useSearch());
  return <PlannerPage searchParams={search} />;
}

export const plannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/planner",
  validateSearch: plannerSearchSchema,
  component: PlannerStandalonePage,
});

// ── /activity ────────────────────────────────────────────────────────────────

function ActivityPage() {
  // No server seed (no SSR). Gate empty-state on the live resource's first fetch
  // by passing an empty seed — ActivityLive falls back until SWR returns.
  // VerificationShell owns the live companion activity surface on `/activity`
  // (same pattern as `/settings`); ActivityLive remains the retained body.
  return (
    <div data-route="activity">
      <VerificationShell>
        <div className="space-y-8 px-6 py-8">
          <PageHeader
            title="Activity"
            description="What just happened across the workspace."
          />
          <ActivityLive seed={[]} />
        </div>
      </VerificationShell>
    </div>
  );
}

export const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/activity",
  component: ActivityPage,
});

// ── /favorites ───────────────────────────────────────────────────────────────

function FavoritesPage() {
  return (
    <div className="flex flex-col">
      <div className="px-6 py-8">
        <PageHeader
          title="Favorites"
          description="Pages you've starred from anywhere"
          className="mb-5"
        />
        <FavoritesListClient />
      </div>
    </div>
  );
}

export const favoritesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/favorites",
  component: FavoritesPage,
});

// ── /suggestions ─────────────────────────────────────────────────────────────

function SuggestionsPage() {
  return (
    <div className="space-y-8 px-6 py-8">
      <PageHeader
        title="Suggestions"
        description="Everything that needs you — surfaced from across the workspace."
      />
      <SuggestionsLive seed={[]} />
    </div>
  );
}

export const suggestionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/suggestions",
  component: SuggestionsPage,
});

// ── /overrides — human_gated / abstain-routed override-as-diff queue ──────────

function OverridesPage() {
  return (
    <div className="space-y-8 px-6 py-8">
      <PageHeader
        title="Override queue"
        description="Human-gated items as a before/after diff, plus signed OverrideRecords of what the human actually changed."
        breadcrumbs={[
          { label: "Work", href: "/work" },
          { label: "Overrides" },
        ]}
      />
      <OverrideQueue />
      <div className="space-y-3">
        <h2 className="type-title font-medium">Recorded overrides</h2>
        <p className="type-subtext text-muted-foreground">
          What the human changed against what the system proposed — signed
          with the audit-checkpoint key.
        </p>
        <RecordedOverrideDiffs />
      </div>
    </div>
  );
}

export const overridesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overrides",
  component: OverridesPage,
});

// ── /verdicts — permanent redirect into the canonical review door ────────────

export const verdictsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verdicts",
  validateSearch: verdictsSearchSchema,
  beforeLoad: ({ location }) => {
    const dest =
      resolveReviewVerdictEvidenceRedirect(location.pathname, location.searchStr) ??
      "/findings";
    throw redirect({ to: dest, replace: true });
  },
});
